import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{10}$/.test(form.mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created successfully");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <p className="overline text-gold-dark mb-2">Join ChainSmart</p>
          <h1 className="font-serif text-4xl text-obsidian mb-8">Create Account</h1>

          {error && <div className="bg-discount/10 border border-discount/30 text-discount text-sm px-4 py-3 mb-5 rounded-sm" data-testid="register-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-sm" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-sm" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input id="mobile" value={form.mobile} onChange={set("mobile")} required maxLength={10} placeholder="10-digit number" className="mt-1.5 rounded-sm" data-testid="register-mobile" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5 rounded-sm" data-testid="register-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-obsidian text-white font-semibold py-3.5 hover:bg-gold hover:text-obsidian transition-colors disabled:opacity-50 rounded-sm" data-testid="register-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-gold-dark font-semibold hover:underline" data-testid="go-login">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative bg-obsidian order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1633555234296-657a18d8b81a?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <img src="/logo.png" alt="ChainSmart" className="h-16  mb-6" />
            <h2 className="font-serif text-4xl text-white leading-tight max-w-sm">Create an account to shop premium chains</h2>
          </div>
        </div>
      </div>
    </div>
  );
}
